import React, { useState, useEffect } from "react"
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  CircularProgress,
} from "@material-ui/core"
import { useHistory } from "react-router-dom"
import {
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  LocalOffer as LocalOfferIcon,
} from "@material-ui/icons"
import { paths } from "../../routes/paths"
import services from "../../services"
import styles from "./AdminDashboardContainer.module.scss"

const AdminDashboardContainer = () => {
  const history = useHistory()
  const [loading, setLoading] = useState(true)
  const [applicationCounts, setApplicationCounts] = useState({
    pending: 0,
    approved: 0,
    declined: 0,
    total: 0,
  })
  const [subscriptionCounts, setSubscriptionCounts] = useState({
    active: 0,
    paused: 0,
    trial: 0,
    none: 0,
    total: 0,
  })
  const [discountCodeCounts, setDiscountCodeCounts] = useState({
    active: 0,
    inactive: 0,
    total: 0,
  })

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setLoading(true)

        const [appResponse, subResponse, discountResponse] = await Promise.all([
          services.userApplicationServices.getApplicationCounts(),
          services.subscriptionAdminServices.getSubscriptionCounts(),
          services.stripeDiscountCodeAdminServices.getDiscountCodes(),
        ])

        if (appResponse && appResponse.data && appResponse.data.data) {
          setApplicationCounts(appResponse.data.data)
        }

        if (subResponse && subResponse.data && subResponse.data.data) {
          setSubscriptionCounts(subResponse.data.data)
        }

        if (discountResponse?.data?.data?.discount_codes) {
          const codes = discountResponse.data.data.discount_codes
          const active = codes.filter((c) => c.active).length
          setDiscountCodeCounts({
            total: codes.length,
            active,
            inactive: codes.length - active,
          })
        }
      } catch (err) {
        console.error("Error fetching dashboard counts:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchCounts()
  }, [])

  const handleApplicationStatusClick = (status) => {
    const url = status
      ? `${paths.dashboardApplications}?status=${status}`
      : paths.dashboardApplications
    history.push(url)
  }

  const handleSubscriptionStatusClick = (status) => {
    const url = status
      ? `${paths.dashboardSubscriptions}?status=${status}`
      : paths.dashboardSubscriptions
    history.push(url)
  }

  const handleDiscountCodeStatusClick = (status) => {
    const url = status
      ? `${paths.dashboardDiscountCodes}?status=${status}`
      : paths.dashboardDiscountCodes
    history.push(url)
  }

  const applicationStatuses = [
    {
      label: "Pending",
      value: applicationCounts.pending,
      status: "pending",
      color: "#ff9800",
    },
    {
      label: "Approved",
      value: applicationCounts.approved,
      status: "approved",
      color: "#4caf50",
    },
    {
      label: "Declined",
      value: applicationCounts.declined,
      status: "rejected",
      color: "#f44336",
    },
    {
      label: "Total",
      value: applicationCounts.total,
      status: "",
      color: "#2196f3",
    },
  ]

  const subscriptionStatuses = [
    {
      label: "Active",
      value: subscriptionCounts.active,
      status: "active",
      color: "#4caf50",
    },
    {
      label: "Paused",
      value: subscriptionCounts.paused,
      status: "paused",
      color: "#ff9800",
    },
    {
      label: "Trial",
      value: subscriptionCounts.trial,
      status: "trial",
      color: "#2196f3",
    },
    {
      label: "None",
      value: subscriptionCounts.none,
      status: "none",
      color: "#757575",
    },
    {
      label: "Total",
      value: subscriptionCounts.total,
      status: "",
      color: "#9c27b0",
    },
  ]

  const discountCodeStatuses = [
    {
      label: "Active",
      value: discountCodeCounts.active,
      status: "active",
      color: "#66bb6a",
    },
    {
      label: "Inactive",
      value: discountCodeCounts.inactive,
      status: "inactive",
      color: "#9e9e9e",
    },
    {
      label: "Total",
      value: discountCodeCounts.total,
      status: "",
      color: "#64b5f6",
    },
  ]

  const sections = [
    {
      id: "applications",
      title: "User Applications",
      description: "Manage user applications, approvals, and rejections",
      icon: AssignmentIcon,
      iconColor: "#64b5f6",
      path: paths.dashboardApplications,
      color: "#64b5f6",
      buttonLabel: "View All",
      statuses: applicationStatuses,
      onStatusClick: handleApplicationStatusClick,
    },
    {
      id: "subscriptions",
      title: "User Subscriptions",
      description: "View and manage user subscription details",
      icon: PaymentIcon,
      iconColor: "#81c784",
      path: paths.dashboardSubscriptions,
      color: "#81c784",
      buttonLabel: "View All",
      statuses: subscriptionStatuses,
      onStatusClick: handleSubscriptionStatusClick,
    },
    {
      id: "discountCodes",
      title: "Stripe Discount Codes",
      description: "Create and manage promotion codes that customers can redeem during checkout.",
      icon: LocalOfferIcon,
      iconColor: "#90caf9",
      path: paths.dashboardDiscountCodes,
      color: "#64b5f6",
      buttonLabel: "View All",
      statuses: discountCodeStatuses,
      onStatusClick: handleDiscountCodeStatusClick,
    },
  ]

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Typography variant="h4" gutterBottom className={styles.title}>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" className={styles.subtitle}>
          Select a section to manage
        </Typography>

        <Grid container spacing={3} className={styles.sectionsGrid}>
          {sections.map((section) => {
            const SectionIcon = section.icon

            return (
              <Grid item xs={12} key={section.id}>
                <Card
                  className={`${styles.sectionCard} ${
                    section.id === "discountCodes" ? styles.sectionCardDiscount : ""
                  }`}
                  style={{
                    borderLeft: `4px solid ${section.color}`,
                  }}
                >
                  <CardContent className={styles.sectionCardContent}>
                    <Box className={styles.sectionHeader}>
                      <Box className={styles.sectionHeaderLeft}>
                        <Box
                          className={styles.sectionIconWrap}
                          style={{
                            color: section.iconColor,
                            borderColor: `${section.color}33`,
                            background: `${section.color}14`,
                          }}
                        >
                          <SectionIcon className={styles.sectionIcon} />
                        </Box>
                        <Box className={styles.sectionInfo}>
                          <Typography variant="h5" className={styles.sectionTitle}>
                            {section.title}
                          </Typography>
                          <Typography variant="body2" className={styles.sectionDescription}>
                            {section.description}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        className={styles.sectionButton}
                        style={{ backgroundColor: section.color }}
                        onClick={() => history.push(section.path)}
                      >
                        {section.buttonLabel}
                      </Button>
                    </Box>

                    {section.statuses.length > 0 && (
                      <Box className={styles.statusesContainer}>
                        <Grid container spacing={2}>
                          {section.statuses.map((statusItem, idx) => (
                            <Grid
                              item
                              xs={6}
                              sm={section.statuses.length > 4 ? 3 : 4}
                              key={idx}
                            >
                              <Box
                                className={styles.statusChip}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  section.onStatusClick(statusItem.status)
                                }}
                                style={{
                                  cursor: "pointer",
                                  borderLeft: `3px solid ${statusItem.color}`,
                                }}
                              >
                                <Typography
                                  className={styles.statusValue}
                                  style={{ color: statusItem.color }}
                                >
                                  {statusItem.value}
                                </Typography>
                                <Typography className={styles.statusLabel}>
                                  {statusItem.label}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </div>
    </div>
  )
}

export default AdminDashboardContainer
